import { ref } from 'vue';
import type { BudgetGroupAthleteItem, BudgetGroupExpanded } from '@/types/budgetGroups';

type ViewKeys = 'INSTITUTIONAL' | 'THIRD_PARTY' | 'TOTAL_EARNINGS' | 'SCHOLARSHIPS' | 'DEPTH_CHART';

const positionFilters = ref<Record<string, string[]>>({});
const budgetPeriodFilters = ref<Record<string, string[]>>({});
const currentView = ref<ViewKeys | null>(null);

/**
 * Whether the athlete has roster data for at least one budget whose `academicYearDateId` is in
 * `selectedBudgetPeriodIds`. These are the same string values stored by `useRosterFilter` and used
 * in `RosterFilter` checkbox values (`academicYearDateId`), not display labels.
 */
export function athleteMatchesRosterBudgetPeriodFilter(
  athlete: BudgetGroupAthleteItem | undefined,
  selectedBudgetPeriodIds: string[],
  budgetGroup: BudgetGroupExpanded | null,
): boolean {
  if (selectedBudgetPeriodIds.length === 0) return true;
  if (!athlete || !budgetGroup) return false;
  return budgetGroup.budgets.some(
    (b) =>
      selectedBudgetPeriodIds.includes(b.twrn)
      && !!athlete.budgets[b.twrn],
  );
}

export function useRosterFilter() {
  const setCurrentView = (view: ViewKeys | null) => {
    currentView.value = view;
    if (view && !positionFilters.value[view]) {
      positionFilters.value[view] = [];
      budgetPeriodFilters.value[view] = [];
    }
  };

  const setPositionFilter = (filter: string[]) => {
    if (!currentView.value) return;
    positionFilters.value[currentView.value] = filter;
  };

  const getPositionFilter = () => {
    return currentView.value ? positionFilters.value[currentView.value] || [] : [];
  };

  const setBudgetPeriodFilter = (filter: string[]) => {
    if (!currentView.value) return;
    budgetPeriodFilters.value[currentView.value] = filter;
  };

  const getBudgetPeriodFilter = () => {
    return currentView.value ? budgetPeriodFilters.value[currentView.value] || [] : [];
  };

  const getFilterCount = () => {
    if (!currentView.value) return 0;
    const positions = positionFilters.value[currentView.value] || [];
    const periods = budgetPeriodFilters.value[currentView.value] || [];
    return positions.length + periods.length;
  };

  const resetFilters = () => {
    positionFilters.value = {};
    budgetPeriodFilters.value = {};
  };

  return {
    currentView,
    positionFilters,
    budgetPeriodFilters,
    setCurrentView,
    setPositionFilter,
    getPositionFilter,
    setBudgetPeriodFilter,
    getBudgetPeriodFilter,
    getFilterCount,
    resetFilters,
  };
}
